import { useMutation, useQuery } from '@tanstack/react-query';
import { del, get, post } from './client';

/**
 * 客户模块 API。
 * 类型定义与本模块函数供页面组件配合 react-query 使用；
 * 客户列表/详情/CRUD 由页面按需以 useQuery/useMutation 消费这些函数。
 */

// ============ 类型定义 ============

/**
 * 客户合并候选项（POST /customers/verify 返回的统一候选结构）。
 * 后端 service 直接产出 camelCase 键，ResponseInterceptor 不再二次转换。
 */
export interface CustomerCandidate {
  id: string;
  fullName: string;
  phone: string | null;
  backupPhone: string | null;
  /** 身份证号，恒为掩码值（明文只存在于后端 PiiFieldService 内部） */
  idCard: string | null;
  email: string | null;
  address?: string | null;
  avatarUrl: string | null;
  status: string | null;
  /** 身份标识：true=系统用户（已注册）/ false=系统客户（未注册） */
  isUser: boolean;
  orderCount: number;
  lastOrderAt: string | null;
  /**
   * 命中字段。取值恒为 snake_case 的数据库列名（id_card / phone / backup_phone /
   * email / full_name）——它们是稳定标识符而非普通文案，拦截器只转键不转值。
   */
  hitFields: string[];
}

export interface VerifyCustomerResponse {
  success: boolean;
  message?: string;
  data: {
    /** none=无重复可直接创建；single=唯一候选；multiple=多候选需人工核对 */
    kind: 'none' | 'single' | 'multiple';
    candidates: CustomerCandidate[];
  };
}

// ============ 模块函数 ============

/**
 * 新建前校验：返回匹配结果（none/single/multiple）+ 统一候选列表
 *
 * 注意：client.post() 内部会执行 unwrapResponse，将后端
 * `{ success, data: { kind, candidates, ... } }` 的壳解掉，
 * 实际返回的就是 `VerifyCustomerResponse['data']`（即 { kind, candidates, ... }）。
 * 因此调用方应直接读取 `.kind` / `.candidates`，不要再访问 `.data.kind`。
 */
export async function verifyCustomer(data: Record<string, unknown>) {
  return post<VerifyCustomerResponse['data']>('/customers/verify', data);
}

/**
 * 将已有客户绑定到当前用户（不新建 party）
 */
export async function linkCustomer(customerId: string, customerData: Record<string, unknown>) {
  return post<{ customerId?: string; success?: boolean; message?: string }>(
    '/customers/link',
    { customerId, customerData }
  );
}

/**
 * 接受客户反向绑定邀请（通知中心入口触发）
 */
export async function acceptBindInvite(inviterUserId: string) {
  return post<{ success: boolean; message: string; customerId: string }>(
    '/customers/bind-reverse',
    { inviterUserId },
  );
}

// ============ 客户订单核对分享 ============

/**
 * 核对页的列：表头 + 是否数字列（右对齐）+ 表尾那一格放哪个合计数。
 * 取值口径全在服务端（`share/customer-share-view.ts` 从 `PDF_COLUMNS` 里筛），
 * 页面只负责排布——所以这里拿到的每个格子已经是最终文本，不再本地格式化金额。
 *
 * 合计靠 `totalKey` 认列而不是按表头文字猜：支出节把余款列叫「待付余款」（方向不同、算法相同），
 * 猜文字的话页面就得再抄一份表头映射，改一处漏一处。
 */
export type ShareDirection = 'income' | 'expense';

export type ShareTotalKey = 'orderAmount' | 'paidAmount' | 'balance';

export interface ShareColumn {
  header: string;
  numeric: boolean;
  totalKey: ShareTotalKey | null;
}

export interface ShareTotals {
  count: number;
  /** 三个金额合计，已格式化到两位小数；键固定这三档，与表尾挂合计的列一一对应 */
  values: Record<ShareTotalKey, string>;
}

export interface ShareTable {
  /** 方向由服务端给，页面据此配色与取汇总——不靠节名中文匹配 */
  direction: ShareDirection;
  /** 节名：收入订单 / 支出订单。空节服务端不下发，所以这里不会出现「支出订单：无」 */
  label: string;
  columns: ShareColumn[];
  /** 与 `columns` 同长同序；空值已是 '-' */
  rows: string[][];
  totals: ShareTotals;
}

/**
 * 核对单只有一批单（2026-10-11 用户裁定：未付款与部分付款合并，不再分「未付清 / 仅未付款」两档）：
 * 服务端收的是「还有未清余款」的那批，已结清、0 元与已取消的不进这一页。
 */
export interface CustomerShareView {
  /** 独立于表格的客户块（姓名 / 公司 / 手机号），表内不再有「联系方式」列 */
  customer: { fullName: string; companyName: string; phone: string };
  /**
   * 收支两节，各自带合计。**载荷有意不给跨两节的总合计**：
   * 收入是「客户欠分享方」、支出是「分享方欠客户」，两个方向的金额相加没有意义
   * （用户 2026-10-11 判的错误计算），所以那个数压根不存在，页面也就无从显示错。
   */
  tables: ShareTable[];
  generatedAt: string;
}

export interface CustomerShareLink {
  shareUrl: string;
  expiresAt: string;
}

/**
 * 生成（或复用）该客户当前有效的核对链接。
 *
 * 与订单分享同一个理由走 query 不走 mutation：弹窗一挂载就要拿到链接，
 * 并发挂载被同一 queryKey 合并成一次请求，比连发两次 POST「后一次撤销前一次」安全。
 * `refetchOnWindowFocus` 必须关：否则每次切回页面都会重新生成并作废上一条已发出去的链接。
 */
export function useCustomerShareLink(customerId: string) {
  return useQuery({
    queryKey: ['customer-share', customerId],
    queryFn: () => post<CustomerShareLink>(`/customers/${customerId}/share`),
    enabled: !!customerId,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** 撤销有效链接。服务端只验登录、不验会员：掉级后更应该还能关掉已发出去的公开入口 */
export function useRevokeCustomerShare() {
  return useMutation({
    mutationFn: (customerId: string) => del<null>(`/customers/${customerId}/share`),
  });
}

/**
 * 公开访问者按令牌读核对页（失效即 404「分享链接无效或已过期」，不重试）。
 * 一次开页只发一次请求：核对单只有一批欠款单，没有档位可切。
 */
export function useCustomerShareView(token: string) {
  return useQuery({
    queryKey: ['customer-share-view', token],
    queryFn: () => get<{ view: CustomerShareView; expiresAt: string }>(`/share/customer/${encodeURIComponent(token)}`),
    enabled: !!token,
    retry: false,
    staleTime: 60_000,
  });
}
